import { createAxios } from '../../lib/axiosFactory';
import { Template } from '../../lib/interfaces/template';
import { dataCollectionParser } from '../../lib/response/dataParser';
import { VuexLocalStorage } from '../../lib/vuexLocalStorage';
import { Commit, Dispatch } from 'vuex';
import { ActionResponse } from '../../lib/interfaces/actionResponse';
import { errorParser } from '../../lib/response/errorParser';
import _ from 'lodash';

const storage = new VuexLocalStorage('_ADMIN_TEMPLATE_');

export const state = {
  templates: [] as Template[],
  templateGoals: [] as string[],
  templateName: String
};

export const mutations = {
  setTemplates(state: any, templates: any[]): void {
    state.templates = templates;
  },
  setTemplateGoals(state: any, templateGoals: any[]): void {
    state.templateGoals = templateGoals;
  },
  setTemplateName(state: any, payload: { templateName: string }): void {
    state.templateName = payload.templateName;
  }
};

export const getters = {
  featuredLimitReached(state): boolean {
    return (
      state.templates.filter(
        (template: Template) => template.featuredAt && !template.archivedAt
      ).length >= 5
    );
  },
  featuredLimitExceeded(state): boolean {
    return (
      state.templates.filter(
        (template: Template) => template.featuredAt && !template.archivedAt
      ).length > 5
    );
  },
  templateGoals(state): string[] {
    return _(state.templates)
      .map('goals')
      .flatten()
      .concat(state.templateGoals)
      .uniq()
      .filter(v => v)
      .value();
  }
};

export const actions = {
  async loadTemplates(
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    {
      endpoint,
      searchCriteria
    }: {
      endpoint?: string;
      searchCriteria?: {
        name: string;
        goals: string[];
        status: 'draft' | 'published' | 'archived';
      };
    } = {}
  ): Promise<void> {
    try {
      let response;

      if (endpoint) {
        response = await createAxios().get(endpoint);
      } else {
        response = await createAxios().get(
          '/backend/mobiz-admin-templates-index',
          {
            params: {
              ...(searchCriteria?.name?.trim?.()?.length && {
                [`filter[name][val]`]: searchCriteria.name,
                [`filter[name][op]`]: 'contains'
              }),
              ...(searchCriteria?.goals?.length && {
                [`filter[goals][val]`]: searchCriteria.goals.join(','),
                [`filter[goals][op]`]: 'arrayContainsOneOf'
              }),
              ...(searchCriteria?.status && {
                [`filter[status][val]`]: searchCriteria.status,
                [`filter[status][op]`]: 'eq'
              })
            }
          }
        );
      }

      let templates: any[] = dataCollectionParser(response);

      templates = await Promise.all(
        templates.map(async template => {
          const response = await dispatch(
            'campaign/getCampaignTemplateImagePreview',
            {
              templateId: template.id,
              isSharedTemplate: true,
              updatedAt: template.updatedAt
            },
            {
              root: true
            }
          );
          const previewImageUrl = response.success
            ? URL.createObjectURL(response.body)
            : require('~/assets/images/template/preview-placeholder.png');
          return { ...template, previewImageUrl };
        })
      );

      commit('setTemplates', templates);
    } catch (error) {
      commit('setTemplates', []);
    }
  },
  async loadTemplateGoals({ commit }: { commit: Commit }): Promise<void> {
    try {
      const response = await createAxios().get(
        '/backend/mobiz-admin-template-goals-index'
      );

      const goals: string[] = response.data.data;

      commit('setTemplateGoals', goals);
    } catch (error) {
      commit('setTemplateGoals', []);
    }
  },

  async bulkUpdateTemplate({ rootState }, data: any): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().patch(
        '/backend/mobiz-admin-bulk-templates-update',
        {
          data
        }
      );

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateTemplate({ rootState }, data: any): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().patch(
        '/backend/mobiz-admin-templates-update',
        {
          data
        },
        {
          params: {
            id: data.id
          }
        }
      );

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createTemplate(
    { commit }: { commit: Commit },
    data: any
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/mobiz-admin-templates-create',
        {
          data
        },
        {
          ...(!data?.id && {
            params: {
              id: data.id
            }
          })
        }
      );
      commit('setTemplateName', {
        templateName: response.data.data.attributes.name
      });
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
